// Self-tape file naming. Casting offices each want a different convention
// ("LASTNAME_FIRSTNAME_ROLE", "Project - First Last", …). A template made of
// {tokens} is rendered against an audition's data when the tape is downloaded,
// so every file leaves RepTree named exactly how the office asked. The same
// engine powers the agency default, a per-audition override, and the instruction
// auto-extracted from a casting email.
/** The agency-wide default when nothing more specific is set. */
export const DEFAULT_FILENAME_TEMPLATE = '{last}_{first}_{role}';
export const FILENAME_TOKENS = [
    { token: '{first}', label: 'First name', sample: 'Jane' },
    { token: '{last}', label: 'Last name', sample: 'Actor' },
    { token: '{full}', label: 'Full name', sample: 'Jane Actor' },
    { token: '{initials}', label: 'Initials', sample: 'JA' },
    { token: '{role}', label: 'Role / character', sample: 'Detective' },
    { token: '{rolesize}', label: 'Role size', sample: 'Co-Star' },
    { token: '{title}', label: 'Project / title', sample: 'Abbott Elementary' },
    { token: '{casting}', label: 'Casting office', sample: 'Allison Jones' },
    { token: '{medium}', label: 'Medium', sample: 'Film TV' },
    { token: '{type}', label: 'Audition type', sample: 'Self Tape' },
    { token: '{date}', label: "Today's date", sample: '2026-06-13' },
    { token: '{due}', label: 'Due date', sample: '2026-06-15' },
    { token: '{take}', label: 'Take number', sample: '1' },
];
const TWO = (n) => String(n).padStart(2, '0');
/** Format a Date with a tiny token grammar (YYYY, YY, MM, DD). Default: YYYY-MM-DD. */
function formatDate(d, fmt) {
    const f = fmt && /[YMD]/.test(fmt) ? fmt : 'YYYY-MM-DD';
    return f
        .replace(/YYYY/g, String(d.getFullYear()))
        .replace(/YY/g, String(d.getFullYear()).slice(-2))
        .replace(/MM/g, TWO(d.getMonth() + 1))
        .replace(/DD/g, TWO(d.getDate()));
}
function splitName(ctx) {
    const full = (ctx.full ?? [ctx.first, ctx.last].filter(Boolean).join(' ')).trim();
    let first = (ctx.first ?? '').trim();
    let last = (ctx.last ?? '').trim();
    if ((!first || !last) && full) {
        const parts = full.split(/\s+/);
        if (!first)
            first = parts[0] ?? '';
        if (!last)
            last = parts.length > 1 ? parts.slice(1).join(' ') : '';
    }
    return { first, last, full };
}
const applyCase = (s, mod) => {
    switch (mod) {
        case 'upper': return s.toUpperCase();
        case 'lower': return s.toLowerCase();
        case 'title': return s.replace(/\b\w/g, (c) => c.toUpperCase());
        default: return s;
    }
};
/**
 * Render a template against an audition. Unknown tokens vanish; empty tokens leave
 * no dangling separators. Returns a base name WITHOUT extension — the caller appends
 * the real file extension so the format is always preserved.
 */
export function renderFilename(template, ctx) {
    const tpl = (template || '').trim() || DEFAULT_FILENAME_TEMPLATE;
    const { first, last, full } = splitName(ctx);
    const initials = [first, last].filter(Boolean).map((p) => p[0].toUpperCase()).join('');
    const now = ctx.date ?? new Date();
    const dueDate = ctx.due ? new Date(`${ctx.due}T00:00:00`) : null;
    const out = tpl.replace(/\{(\w+)(?::([\w-]+))?\}/g, (_m, name, mod) => {
        switch (name.toLowerCase()) {
            case 'first': return applyCase(first, mod);
            case 'last': return applyCase(last, mod);
            case 'full':
            case 'name': return applyCase(full, mod);
            case 'initials': return initials;
            case 'role':
            case 'character': return applyCase(ctx.role ?? '', mod);
            case 'rolesize': return applyCase(ctx.roleSize ?? '', mod);
            case 'title':
            case 'project': return applyCase(ctx.title ?? '', mod);
            case 'casting': return applyCase(ctx.casting ?? '', mod);
            case 'medium': return applyCase(ctx.medium ?? '', mod);
            case 'type': return applyCase(ctx.type ?? '', mod);
            case 'date': return formatDate(now, mod);
            case 'due': return dueDate ? formatDate(dueDate, mod) : '';
            case 'take': return ctx.take != null ? String(ctx.take) : '';
            default: return '';
        }
    });
    return sanitizeFilename(out) || sanitizeFilename(ctx.title ?? '') || 'self-tape';
}
const FILENAME_MAX = 180;
/**
 * Make a string safe across Windows/macOS/Linux: drop illegal characters, collapse
 * repeated separators left behind by empty tokens, and trim stray separators. Also drops the
 * Unicode direction overrides that can disguise an extension ("tape‮4pm.exe" → looks like .mp4),
 * and bounds the input first so the passes below stay cheap on hostile lengths. A name over the
 * length cap loses the end of its STEM, never its extension ("…a very long title.mp4" stays an .mp4).
 */
export function sanitizeFilename(name) {
    const clean = tidyFilename(name.slice(0, 1000));
    if (name.length <= 1000 && clean.length <= FILENAME_MAX)
        return clean;
    const ext = /\.[A-Za-z0-9]{1,8}$/.exec(name)?.[0] ?? '';
    const stem = (ext && clean.endsWith(ext) ? clean.slice(0, -ext.length) : clean)
        .slice(0, FILENAME_MAX - ext.length)
        .replace(/[\s._-]+$/, '');
    return stem ? stem + ext : clean.slice(0, FILENAME_MAX);
}
function tidyFilename(name) {
    return name
        .replace(/[\\/:*?"<>|\x00-\x1f\x7f]/g, '') // illegal on common filesystems
        .replace(/[\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '') // bidi controls
        .replace(/\s+/g, ' ')
        .replace(/ ?([_-]) ?/g, '$1') // tidy separators around removed pieces
        .replace(/_{2,}/g, '_')
        .replace(/-{2,}/g, '-')
        .replace(/\.{2,}/g, '.')
        .replace(/^[\s._-]+|[\s._-]+$/g, '');
}
//# sourceMappingURL=filename.js.map